import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
export function useFlexMenu(items, itemGap) {
    const list = useRef(null);
    const showMoreButtom = useRef(null);
    const [firstRender, setFirstRender] = useState(true);
    const [moreButtonWidth, setMoreButtonWidth] = useState(0);
    const [listWidth, setListWidth] = useState(0);
    const [itemsWidth, setItemsWidth] = useState([]);
    const [showUpUntil, setShowUpUntil] = useState(0);
    const [mouseOverMoreButton, setMouseOverMoreButton] = useState(false);
    const [mouseOverHiddenLinks, setMouseOverHiddenLinks] = useState(false);
    const headerMounted = useRef(false);
    /* EFFECTS */
    useLayoutEffect(() => {
        headerMounted.current = true;
        // Save new width of "More" button
        if (showMoreButtom.current != null) {
            setMoreButtonWidth(showMoreButtom.current.offsetWidth);
        }
        // Save new width of the container of the items
        if (list.current != null) {
            setListWidth(list.current.offsetWidth);
        }
        // @ts-ignore Experimental API
        document.fonts.ready.then(() => {
            if (headerMounted.current) {
                // Save all new widths of the invisible items
                setItemsWidth(getInvisibleItemsWidth(itemGap));
            }
        });
        return () => {
            headerMounted.current = false;
        };
    }, [itemGap, items]);
    useLayoutEffect(() => {
        if (itemsWidth.length > 0) {
            // Save the new index where the list of items is going
            // to be separated between visible and hidden items
            setShowUpUntil(getFirstHiddenItemIndex(listWidth, itemsWidth, moreButtonWidth));
            if (firstRender) {
                setFirstRender(false);
            }
        }
    }, [itemsWidth, listWidth, moreButtonWidth, firstRender]);
    /* CALLBACKS */
    const mouseEnterMoreButton = useCallback(() => {
        setMouseOverMoreButton(true);
    }, []);
    const mouseLeaveMoreButton = useCallback(() => {
        setMouseOverMoreButton(false);
    }, []);
    const mouseEnterHiddenLinks = useCallback(() => {
        if (mouseOverMoreButton) {
            setMouseOverHiddenLinks(true);
        }
    }, [mouseOverMoreButton]);
    const mouseLeaveHiddenLinks = useCallback(() => {
        setMouseOverHiddenLinks(false);
    }, []);
    /* CONSTANTS */
    const moreButtonHandlers = useMemo(() => ({
        onMouseEnter: mouseEnterMoreButton,
        onMouseLeave: mouseLeaveMoreButton,
    }), [mouseEnterMoreButton, mouseLeaveMoreButton]);
    const hiddenLinksHandlers = useMemo(() => ({
        onMouseEnter: mouseEnterHiddenLinks,
        onMouseLeave: mouseLeaveHiddenLinks,
    }), [mouseEnterHiddenLinks, mouseLeaveHiddenLinks]);
    const showHiddenLinks = useMemo(() => {
        return mouseOverMoreButton || mouseOverHiddenLinks;
    }, [mouseOverHiddenLinks, mouseOverMoreButton]);
    const visibleLinks = useMemo(() => items.slice(0, showUpUntil), [
        items,
        showUpUntil,
    ]);
    const hiddenLinks = useMemo(() => items.slice(showUpUntil), [
        items,
        showUpUntil,
    ]);
    const isMoreButtonVisible = useMemo(() => (firstRender || hiddenLinks.length > 0) && items.length > 0, [firstRender, hiddenLinks.length, items.length]);
    /* RESULTS */
    return useMemo(() => ({
        listRef: list,
        showMoreButtonRef: showMoreButtom,
        isMoreButtonVisible,
        visibleLinks,
        hiddenLinks,
        showHiddenLinks,
        moreButtonHandlers,
        hiddenLinksHandlers,
    }), [
        hiddenLinks,
        hiddenLinksHandlers,
        isMoreButtonVisible,
        moreButtonHandlers,
        showHiddenLinks,
        visibleLinks,
    ]);
}
function getInvisibleItemsWidth(itemGap) {
    const invisibleItems = document.querySelectorAll(".invisible-link");
    if (invisibleItems != null && invisibleItems.length > 0) {
        const widthsTemp = [];
        invisibleItems.forEach(invisibleItem => {
            widthsTemp.push(invisibleItem.offsetWidth + itemGap);
        });
        return widthsTemp;
    }
    else {
        return [];
    }
}
function getFirstHiddenItemIndex(listWidth, itemsWidth, showMoreButtonWidth) {
    let totalWidthOfItems = itemsWidth.reduce((prev, curr) => prev + curr, 0);
    const lasLinkIndex = itemsWidth.length - 1;
    if (totalWidthOfItems > listWidth) {
        totalWidthOfItems += showMoreButtonWidth;
        for (let i = lasLinkIndex; i >= 0; i--) {
            if (totalWidthOfItems - itemsWidth[i] <= listWidth) {
                return i;
            }
            else {
                totalWidthOfItems -= itemsWidth[i];
            }
        }
        return 0;
    }
    else {
        return lasLinkIndex + 1;
    }
}
//# sourceMappingURL=useFlexMenu.js.map